import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  DirtyFormAlert,
  Label,
  InputErrorMessage,
  Modal,
  MultiEmailInput,
  Notification,
} from 'shared/components';
import { errorMsg } from 'shared/constants';
import { hasPermission } from 'shared/helpers';
import { fetchLicenseUserDevicesOnLicense } from '../../actions';
import { bulkDeviceReset } from '../../LicenseContainer/LicenseDevices/actions';
import { removeLicenseUser } from '../RemoveLicenseUserForm/actions';
import '../BatchLicenseAssign/styles.scss';
import './styles.scss';

const DEVICES_PAGE_SIZE = 100;

const normalizeEmail = (email) =>
  String(email || '')
    .trim()
    .toLowerCase();

const BatchLicenseRemove = ({ closeCb, confirmCb, license }) => {
  const { t } = useTranslation();
  const licenseID = get(license, 'id');
  const assignedUsers = get(license, 'license_users', []);
  const permissionsList = useSelector((state) =>
    get(state, 'user.details.permissions'),
  );
  const canResetDevices = hasPermission(permissionsList, 'reset_device');

  const [dirty, setDirty] = useState(false);
  const [isDirtyFormAlertDisplayed, setDirtyFormAlertDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [licenseUsers, setLicenseUsers] = useState([]);
  const [resetDevices, setResetDevices] = useState(false);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(null);

  const getActiveDeviceIDs = async (licenseUserID) => {
    const ids = [];
    let offset = 0;
    let count = 0;
    do {
      const res = await fetchLicenseUserDevicesOnLicense(
        licenseID,
        licenseUserID,
        offset,
        DEVICES_PAGE_SIZE,
      );
      count = get(res, 'data.count', 0);
      get(res, 'data.results', [])
        .filter(
          (device) =>
            device.device_active &&
            String(device.license) === String(licenseID),
        )
        .forEach((device) => ids.push(device.id));
      offset += DEVICES_PAGE_SIZE;
    } while (offset < count);
    return ids;
  };

  const removeUsers = async (emails) => {
    const removed = [];
    const notAssigned = [];
    const failed = [];
    const resetFailed = [];
    // Removed users with no active device linked to them on this license, e.g.
    // on older licenses whose devices aren't linked to users
    const noLinkedDevices = [];
    let devicesReset = 0;

    // One request per user keeps each removal independent: a failure is
    // reported for that email and the rest still go through.
    for (const [index, email] of emails.entries()) {
      setProgress({ done: index, total: emails.length });
      const user = assignedUsers.find(
        (u) => normalizeEmail(get(u, 'true_email')) === normalizeEmail(email),
      );
      if (!user) {
        notAssigned.push(email);
      } else {
        let deviceIDs = [];
        let isRemoved = false;
        try {
          // Unassigning the user unlinks their devices, so look them up first,
          // but reset only after the removal succeeded
          if (resetDevices) {
            deviceIDs = await getActiveDeviceIDs(user.id);
          }
          await removeLicenseUser(licenseID, user.id);
          isRemoved = true;
          if (deviceIDs.length) {
            await bulkDeviceReset(deviceIDs);
            devicesReset += deviceIDs.length;
          } else if (resetDevices) {
            noLinkedDevices.push(email);
          }
          removed.push(email);
        } catch (err) {
          if (isRemoved) {
            resetFailed.push(email);
          } else {
            failed.push(email);
          }
        }
      }
    }
    setProgress(null);

    return {
      removed,
      notAssigned,
      failed,
      resetFailed,
      noLinkedDevices,
      devicesReset,
    };
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (results) {
      return closeCb();
    }

    if (licenseUsers.value) {
      setLicenseUsers({
        ...licenseUsers,
        validationError: `${get(errorMsg, 'unsubmittedEmail')} (${get(licenseUsers, 'value')})`,
      });
      return false;
    }

    // The email input only drops exact duplicates, while matching ignores case
    const emails = get(licenseUsers, 'emails', []).filter(
      (email, index, list) =>
        list.findIndex((e) => normalizeEmail(e) === normalizeEmail(email)) ===
        index,
    );
    if (!emails.length) {
      return false;
    }

    setLoading(true);
    const outcome = await removeUsers(emails);
    setLoading(false);

    if (outcome.removed.length || outcome.resetFailed.length) {
      confirmCb();
    }

    if (
      !outcome.notAssigned.length &&
      !outcome.failed.length &&
      !outcome.resetFailed.length &&
      !outcome.noLinkedDevices.length
    ) {
      Notification(
        'success',
        t('Changes saved successfully'),
        resetDevices
          ? t('License users have been removed and their devices reset')
          : t('License users have been removed'),
      );
      return closeCb();
    }

    setResults(outcome);
    return true;
  };

  const handleEmailSubmit = (val) => {
    setDirty(true);
    setLicenseUsers(val);
  };

  const handleClose = () => {
    if (loading) {
      return false;
    }
    if (!dirty || results) {
      return closeCb();
    }
    return setDirtyFormAlertDisplay(true);
  };

  const renderResultGroup = (title, emails, className) =>
    emails.length > 0 && (
      <div className={`BatchLicenseRemove-result ${className}`}>
        <div className="result-title">{`${title} (${emails.length})`}</div>
        <ul>
          {emails.map((email) => (
            <li key={email}>{email}</li>
          ))}
        </ul>
      </div>
    );

  return (
    <Modal
      title={t('Batch remove users')}
      closeCb={handleClose}
      confirmCb={handleSubmit}
      confirmText={results ? t('Close') : t('Remove users')}
      confirmTheme={results ? 'success' : 'error'}
      disabled={loading}
      size="lg"
    >
      <div className="BatchLicenseAssign BatchLicenseRemove">
        <div className="BatchLicenseAssign-main">
          <div className="unassigned-count">
            {`${t('Assigned users')}:`}
            &nbsp;
            <span>{assignedUsers.length}</span>
          </div>
          {results && (
            <div className="BatchLicenseRemove-results">
              {resetDevices && (
                <div className="BatchLicenseRemove-devices-reset">
                  {`${t('Devices reset')}: ${results.devicesReset}`}
                </div>
              )}
              {renderResultGroup(t('Removed'), results.removed, 'is-removed')}
              {renderResultGroup(
                t('Removed, but devices could not be reset'),
                results.resetFailed,
                'is-failed',
              )}
              {renderResultGroup(
                t('Removed, no linked devices found to reset'),
                results.noLinkedDevices,
                'is-skipped',
              )}
              {results.noLinkedDevices.length > 0 && (
                <div className="BatchLicenseRemove-note">
                  {t(
                    'Only devices linked to a user are reset. On older licenses devices may not be linked to users; reset those from the Devices tab.',
                  )}
                </div>
              )}
              {renderResultGroup(
                t('Not assigned to this license'),
                results.notAssigned,
                'is-skipped',
              )}
              {renderResultGroup(
                t('Could not be removed'),
                results.failed,
                'is-failed',
              )}
            </div>
          )}
          {!results && assignedUsers.length > 0 && (
            <div>
              <Label text={t('User emails')} inputId="emails-input" />
              <MultiEmailInput
                emails={get(licenseUsers, 'emails')}
                onEmailSubmit={(val) => handleEmailSubmit(val)}
                isDisabled={loading}
              />
              <InputErrorMessage
                text={get(licenseUsers, 'validationError', '')}
              />
              {canResetDevices && (
                <Checkbox
                  label={t(
                    "Also reset the removed users' devices on this license",
                  )}
                  inputId="batch-remove-reset-devices"
                  checked={resetDevices}
                  disabled={loading}
                  handleChange={(val) => setResetDevices(val)}
                />
              )}
            </div>
          )}
          {progress && (
            <div className="BatchLicenseRemove-progress">
              {`${t('Processing')} ${progress.done + 1} / ${progress.total}`}
            </div>
          )}
          {!results && !assignedUsers.length && (
            <div className="warning-notice">
              {t('No users are assigned to this license.')}
            </div>
          )}
        </div>
      </div>
      {isDirtyFormAlertDisplayed && (
        <DirtyFormAlert
          dirty={dirty}
          closeAlert={() => setDirtyFormAlertDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

BatchLicenseRemove.propTypes = {
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
  license: PropTypes.object.isRequired,
};

export default BatchLicenseRemove;
